import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShieldCheck, Lock, AlertTriangle, Loader2, Activity, Radar, Gauge,
} from "lucide-react";

// Image de héros — DÉCORATIVE (bouclier européen + drapeaux). Servie depuis /public.
const HERO = `${process.env.PUBLIC_URL || ""}/assets/images/cros-europe-cyber-hero.webp`;

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex flex-col lg:grid lg:grid-cols-[1.1fr_0.9fr] bg-background">
      {/* ── Section HERO — page d'accueil CROS ── */}
      <section
        className="cros-hero relative flex flex-col justify-center overflow-hidden min-h-[720px] lg:min-h-[620px] px-8 py-16 lg:px-14 xl:px-16 text-white"
        data-testid="home-hero"
      >
        {/* Image décorative : chargement prioritaire (page d'accueil), sans déformation. */}
        <img
          src={HERO}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          draggable="false"
          className="absolute inset-0 h-full w-full object-cover [object-position:center_right] select-none pointer-events-none"
        />

        {/* Voile dégradé — desktop : très sombre à gauche → transparent à droite (bouclier visible). */}
        <div
          aria-hidden="true"
          className="absolute inset-0 hidden lg:block"
          style={{
            background:
              "linear-gradient(to right, rgba(6,16,31,0.92) 0%, rgba(6,16,31,0.86) 30%, rgba(6,16,31,0.55) 62%, rgba(6,16,31,0.12) 100%)",
          }}
        />
        {/* Voile — mobile/tablette : renforcé et uniforme pour la lisibilité du texte centré. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 lg:hidden"
          style={{
            background:
              "linear-gradient(to bottom, rgba(6,16,31,0.90) 0%, rgba(6,16,31,0.88) 100%)",
          }}
        />

        {/* Contenu — aligné à gauche sur ordinateur, centré sur mobile. */}
        <div className="relative z-10 mx-auto lg:mx-0 w-full max-w-xl text-center lg:text-left">
          <div className="flex items-center justify-center lg:justify-start gap-3 mb-6">
            <div className="h-11 w-11 rounded-sm bg-primary flex items-center justify-center shadow-lg shadow-primary/30">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300">
              Plateforme de cyber-résilience
            </span>
          </div>

          <h1 className="font-head font-extrabold tracking-tight leading-[1.02] text-6xl md:text-5xl lg:text-7xl">
            CROS
          </h1>
          <p className="mt-3 font-mono uppercase tracking-[0.22em] text-cyan-400 text-sm md:text-base">
            Cyber resilience orchestration system
          </p>
          <p className="mt-6 text-base md:text-lg text-slate-200/90 leading-relaxed max-w-lg mx-auto lg:mx-0">
            Anticipez les crises cyber, simulez leur propagation et mesurez la résilience de vos
            infrastructures critiques.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center lg:justify-start gap-3">
            <Button
              onClick={() => navigate("/scenarios")}
              data-testid="hero-cta-simulation"
              className="rounded-sm h-11 px-5 gap-2 bg-primary text-white hover:bg-primary/90 motion-safe:transition-colors"
            >
              <Activity className="h-4 w-4" /> Lancer une simulation
            </Button>
            <Button
              onClick={() => navigate("/twin")}
              data-testid="hero-cta-twin"
              variant="outline"
              className="rounded-sm h-11 px-5 gap-2 border-cyan-400/50 bg-white/5 text-white hover:bg-cyan-400/10 hover:border-cyan-300 motion-safe:transition-colors"
            >
              <Radar className="h-4 w-4 text-cyan-300" /> Explorer le jumeau numérique
            </Button>
            <Button
              onClick={() => navigate("/")}
              data-testid="hero-cta-score"
              variant="outline"
              className="rounded-sm h-11 px-5 gap-2 border-white/25 bg-white/5 text-white hover:bg-white/10 motion-safe:transition-colors"
            >
              <Gauge className="h-4 w-4 text-cyan-300" /> Consulter le score de résilience
            </Button>
          </div>

          <div className="mt-10 hidden lg:block font-mono text-[11px] text-slate-400 tracking-wide">
            v0.2 · Argon2id + JWT · audit hash-chaîné
          </div>
        </div>
      </section>

      {/* ── Authentification ── */}
      <div className="flex items-center justify-center p-8 relative">
        <div className="w-full max-w-sm">
          <div className="rounded-sm border border-border bg-card/70 backdrop-blur-sm p-8 shadow-lg shadow-black/30">
            <div className="mb-7">
              <div className="inline-flex items-center gap-1.5 mb-3 px-2 py-1 rounded-sm border border-primary/25 bg-primary/10">
                <Lock className="h-3 w-3 text-primary" />
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Accès sécurisé</span>
              </div>
              <h2 className="font-head text-2xl font-bold tracking-tight">Connexion à la console</h2>
              <p className="text-sm text-muted-foreground mt-1.5">Identifiez-vous pour accéder à CROS.</p>
            </div>

            {error && (
              <div
                className="flex items-start gap-2 mb-5 p-3 rounded-sm border border-destructive/40 bg-destructive/10 text-sm text-red-400"
                data-testid="login-error"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5 rounded-sm font-mono focus-visible:ring-primary"
                  placeholder="vous@organisation.fr"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Mot de passe
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-1.5 rounded-sm font-mono focus-visible:ring-primary"
                  placeholder="••••••••••••"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-sm gap-2 h-10 motion-safe:transition-colors"
                data-testid="login-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                {loading ? "Connexion…" : "Se connecter"}
              </Button>
            </form>
          </div>

          <p className="text-xs text-muted-foreground mt-6 leading-relaxed px-1">
            Verrouillage après 5 tentatives échouées. Chaque connexion est inscrite au journal
            d'audit hash-chaîné et vérifiable.
          </p>
        </div>
      </div>
    </div>
  );
}
